import { OPEN_ENDED_FPS, type ClockSample, type ClockTiming, type FrameClock } from '../animation/clock';
import { frameAtTime } from '../animation/timeline';
import { Observable } from '../lib/store';

/**
 * The single authoritative timebase of an audio-visual session (Phase 4A).
 *
 * Position is computed, never accumulated:
 *
 *     position = fold(originSec + (now − originAt))
 *
 * where `now` comes from ONE source at a time:
 *  - `audio`: the AudioContext time being heard (`AudioEngine.audibleTime()`), anchored at
 *    the context time at which the transport starts playing `originSec`. Audio and visuals
 *    therefore read the same hardware clock; nothing corrects or nudges one toward the other.
 *  - `system`: `performance.now()` — the explicit fallback while the session is muted, the
 *    browser blocks audio, or the context is unavailable.
 * Switching source happens only on transitions (play, pause, seek, restart), each of which
 * re-anchors the origin. `fold` applies the loop range (the same tick range the Tone
 * transport loops), so a looping session wraps on the same instant in sound and picture.
 *
 * The animation frame shown at a session position is the animation's own frame at
 * `position − animationStart` (its fps and loop mode, `frameAtTime`); before the animation
 * starts its first frame is shown.
 *
 * This class implements the renderer's `FrameClock` interface; it is separate from the
 * Phase 3B `PlaybackClock`, which keeps driving the Animation workspace on its own.
 */
export type ClockSource = 'audio' | 'system';

export interface SessionTiming {
  /** Session length in seconds. */
  durationSec: number;
  /** Loop range in seconds, or null. */
  loop: { start: number; end: number } | null;
  /** Seconds at which the animation's frame 0 plays. */
  animationStartSec: number;
  /** Timing of the linked animation (null: open-ended frame counter). */
  animation: ClockTiming | null;
}

export interface SessionClockState {
  playing: boolean;
  source: ClockSource;
  /** Session position (seconds) at `originAt`. */
  originSec: number;
  /** Time (seconds, in the source's timebase) at which `originSec` plays. */
  originAt: number;
  timing: SessionTiming;
  revision: number;
}

/** Folds an unwrapped position into a loop range (identical to the transport's tick loop). */
export function foldPosition(seconds: number, loop: SessionTiming['loop']): number {
  if (!loop || seconds < loop.end) return seconds;
  const len = loop.end - loop.start;
  if (len <= 0) return loop.start;
  return loop.start + ((seconds - loop.end) % len);
}

const DEFAULT_TIMING: SessionTiming = { durationSec: 0, loop: null, animationStartSec: 0, animation: null };

export class SessionClock extends Observable<SessionClockState> implements FrameClock {
  private audioNow: (() => number | null) | null = null;

  constructor(private readonly systemNow: () => number = () => performance.now() / 1000) {
    super({ playing: false, source: 'system', originSec: 0, originAt: 0, timing: DEFAULT_TIMING, revision: 0 });
  }

  /** The source's current time in seconds (the audio source falls back to the last origin). */
  private now(): number {
    const s = this.get();
    if (s.source === 'audio') return this.audioNow?.() ?? s.originAt;
    return this.systemNow();
  }

  /** Session position in seconds (folded into the loop). */
  seconds(): number {
    return this.secondsAt(this.now());
  }

  /** Session position at a time of the current source's timebase. */
  secondsAt(sourceTime: number): number {
    const s = this.get();
    if (!s.playing) return s.originSec;
    // Before the anchored start instant (the scheduling lead) the position holds.
    return foldPosition(s.originSec + Math.max(0, sourceTime - s.originAt), s.timing.loop);
  }

  sample(): ClockSample {
    const seconds = this.seconds();
    const t = this.get().timing;
    const ended = !t.loop && this.get().playing && seconds >= t.durationSec;
    const local = seconds - t.animationStartSec;
    if (!t.animation) return { seconds, frame: Math.floor(Math.max(0, local) * OPEN_ENDED_FPS + 1e-6), ended };
    return { seconds, frame: frameAtTime(Math.max(0, local), t.animation).frame, ended };
  }

  /** Sets the session timing (keeps the position, clamped to the new length). */
  setTiming(timing: SessionTiming): void {
    const s = this.get();
    const position = Math.min(Math.max(0, this.seconds()), Math.max(0, timing.durationSec));
    this.set({ ...s, timing, originSec: position, originAt: s.playing ? this.now() : s.originAt, revision: s.revision + 1 });
  }

  /**
   * Starts running from `fromSec`. `audio` anchors to the AudioContext: `at` is the audible
   * context time at which `fromSec` plays and `audioNow` reads the audible time.
   */
  start(fromSec: number, source: { kind: 'system' } | { kind: 'audio'; at: number; audioNow: () => number | null }): void {
    const s = this.get();
    if (source.kind === 'audio') {
      this.audioNow = source.audioNow;
      this.set({ ...s, playing: true, source: 'audio', originSec: fromSec, originAt: source.at, revision: s.revision + 1 });
    } else {
      this.audioNow = null;
      this.set({ ...s, playing: true, source: 'system', originSec: fromSec, originAt: this.systemNow(), revision: s.revision + 1 });
    }
  }

  /** Stops at the current position. */
  pause(): void {
    const s = this.get();
    if (!s.playing) return;
    const position = this.seconds();
    this.audioNow = null;
    this.set({ ...s, playing: false, source: 'system', originSec: position, originAt: this.systemNow(), revision: s.revision + 1 });
  }

  /** Moves to a position (clamped to the session); a running clock keeps running on the system source until re-anchored. */
  seek(seconds: number): void {
    const s = this.get();
    const position = Math.min(Math.max(0, Number.isFinite(seconds) ? seconds : 0), s.timing.durationSec);
    this.set({ ...s, originSec: position, originAt: s.playing ? this.now() : s.originAt, revision: s.revision + 1 });
  }

  /** FrameClock: silent playback from the current position (the controller normally starts the clock). */
  play(): void {
    if (!this.get().playing) this.start(this.seconds(), { kind: 'system' });
  }

  reset(): void {
    this.seek(0);
  }

  /** The session reached its end (no loop): stop on the last instant. */
  finish(): void {
    const s = this.get();
    this.audioNow = null;
    this.set({ ...s, playing: false, source: 'system', originSec: s.timing.durationSec, originAt: this.systemNow(), revision: s.revision + 1 });
    this.onFinish?.();
  }

  /** Set by the controller to stop audio when the session ends. */
  onFinish: (() => void) | null = null;
}
